import { Dialog } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Icon } from "@iconify/react";
import { Box, HStack, VStack } from "../../components/utils";
import { CompanyDocument } from "../../types/UserDetailModel";

type Props = {
  open: boolean;
  company: { uuid: string; nameOfTheCompany: string } | null;
  documents: CompanyDocument[];
  onClose: () => void;
};

const FILE_ICONS: {
  extensions: string[];
  icon: string;
  color: string;
  bg: string;
}[] = [
  {
    extensions: ["xlsx", "xlsm", "xls", "csv"],
    icon: "mdi:file-excel-outline",
    color: "text-green-600",
    bg: "bg-green-50",
  },
  {
    extensions: ["doc", "docx"],
    icon: "mdi:file-word-outline",
    color: "text-blue-600",
    bg: "bg-blue-50",
  },
  {
    extensions: ["pdf"],
    icon: "mdi:file-pdf-box",
    color: "text-red-600",
    bg: "bg-red-50",
  },
  {
    extensions: ["png", "jpg", "jpeg", "gif", "svg"],
    icon: "mdi:file-image-outline",
    color: "text-purple-600",
    bg: "bg-purple-50",
  },
];

const getFileIcon = (name: string) => {
  const extension = name?.split(".").pop()?.toLowerCase() || "";
  const match = FILE_ICONS.find((entry) => entry.extensions.includes(extension));
  return match || { icon: "mdi:file-outline", color: "text-stone-500", bg: "bg-stone-100" };
};

const formatBytes = (bytes?: number) => {
  if (!bytes || bytes <= 0) return "-";
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(2)} ${sizes[i]}`;
};

export default function ViewDocumentsModal({
  open,
  company,
  documents,
  onClose,
}: Props) {
  const rows = documents || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box className="p-5">
        <HStack className="items-center justify-between pb-1">
          <VStack className="gap-0.5">
            <p className="text-base font-semibold text-stone-800">
              Documents
            </p>
            {company && (
              <p className="text-xs text-stone-400">
                {company.nameOfTheCompany}
              </p>
            )}
          </VStack>
          <XMarkIcon
            className="h-6 w-6 shrink-0 cursor-pointer text-stone-500 hover:text-stone-700"
            onClick={onClose}
          />
        </HStack>

        <VStack className="gap-2.5 pt-4 h-[250px] overflow-y-auto pr-1">
          {rows.length === 0 && (
            <VStack className="h-full items-center justify-center gap-2">
              <Icon icon="mdi:file-search-outline" height={32} className="text-stone-300" />
              <p className="text-sm text-stone-400">
                No documents found for this company
              </p>
            </VStack>
          )}

          {rows.map((doc) => {
            const { icon, color, bg } = getFileIcon(doc.name);
            return (
              <HStack
                key={doc.id}
                className="group items-center justify-between rounded-xl border border-stone-200 bg-white px-3.5 py-3 transition hover:border-stone-300 hover:shadow-sm"
              >
                <HStack className="items-center gap-3 min-w-0">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${bg}`}>
                    <Icon icon={icon} height={22} className={color} />
                  </div>
                  <VStack className="min-w-0 gap-0.5">
                    <p
                      className="text-sm font-medium text-stone-700 truncate max-w-xs"
                      title={doc.name}
                    >
                      {doc.name}
                    </p>
                    <p className="text-xxs text-stone-400">
                      {formatBytes(doc.size)}
                    </p>
                  </VStack>
                </HStack>
                <button
                  type="button"
                  onClick={() => window.open(doc.key, "_blank")}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-stone-400 transition hover:bg-primary/10 hover:text-primary"
                >
                  <Icon icon="mdi:download-outline" height={19} />
                </button>
              </HStack>
            );
          })}
        </VStack>
      </Box>
    </Dialog>
  );
}
